import { DeviceDataToUpload } from "@/models/DeviceDataToUpload";
import { MedicalDeviceDTO } from "@/models/DeviceModel";
import { ProgressMixin } from "@/utils/ProgressMixin";
import { Sentry } from "@/utils/sentryUtils";

export type RawData = {
  device: MedicalDeviceDTO;
  raw: string;
};

export type OnRawData = (raw: RawData) => void;
export type OnData = (data: DeviceDataToUpload) => void;
export type OnError = (message: string) => void;

export abstract class DeviceDriver<Info, Data> extends ProgressMixin {
  abstract name: string;
  abstract connect: () => Promise<boolean>;
  abstract retrieveInfo: () => Promise<Info>;
  abstract retrieveEvents: (configInfo: Info) => Promise<Data>;
  abstract buildImportRawInfo: (configInfo: Info, data: Data) => RawData | undefined;
  abstract disconnect: () => Promise<boolean>;
  abstract transformForUpload: (data: Data) => DeviceDataToUpload;

  fetchAndUpload = async (onData: OnData, onError: OnError, onRawData: OnRawData) => {
    const connected = await this.connect();
    if (connected) {
      try {
        const info = await this.retrieveInfo();
        const data = await this.retrieveEvents(info);
        onData(this.transformForUpload(data));
        const raw = this.buildImportRawInfo(info, data);
        if (raw) {
          onRawData(raw);
        }
      } catch (e) {
        Sentry.captureException(e);
        onError("devices.errors.dataRead");
      } finally {
        await this.disconnect();
      }
    } else {
      onError("devices.errors.connectionFailure");
    }
  };
}
